declare global {
  interface Window {
    CulqiCheckout?: new (
      publicKey: string,
      config: Record<string, unknown>
    ) => {
      culqi: () => void;
      open: () => void;
      token?: { id: string };
      order?: unknown;
      error?: { user_message?: string };
    };
  }
}

const CHECKOUT_JS_SRC = "https://js.culqi.com/checkout-js";

function loadCheckoutJs(): Promise<void> {
  if (window.CulqiCheckout) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = CHECKOUT_JS_SRC;
    script.onload = () => resolve();
    script.onerror = () =>
      reject(new Error("Failed to load Culqi checkout-js"));
    document.head.appendChild(script);
  });
}

type CulqiCheckoutOptions = {
  publicKey: string;
  amountCents?: number;
  currency: string;
  title?: string;
  description?: string;
  email?: string;
  modal?: boolean;
  container?: string;
  hiddenCulqiLogo?: boolean;
  hiddenBanner?: boolean;
};

async function loadCulqiCheckout({
  onComplete,
  onError,
  ...options
}: CulqiCheckoutOptions & {
  onComplete: (token: string) => void;
  onError?: (error: Error) => void;
}) {
  await loadCheckoutJs();

  const checkout = new window.CulqiCheckout!(options.publicKey, {
    settings: {
      title: options.title,
      currency: options.currency,
      amount: options.amountCents,
    },
    client: {
      email: options.email,
    },
    appearance: {
      logo: "https://brickgestor.app/icon.png",
      hiddenCulqiLogo: options.hiddenCulqiLogo ?? true,
      hiddenBanner: options.hiddenBanner,
      hiddenEmail: true,
      menuType: "sliderTop",
      defaultStyle: {
        buttonBackground: "#fdc700",
      },
    },
    options: {
      lang: "auto",
      modal: options.modal ?? true,
      container: options.container,
      paymentMethods: {
        tarjeta: true,
        yape: false,
        bancaMovil: false,
        agente: false,
        billetera: false,
        cuotealo: false,
      },
    },
  });

  checkout.culqi = function () {
    if (checkout.token) {
      onComplete(checkout.token.id);
    } else if (checkout.error) {
      onError?.(
        new Error(checkout.error.user_message || "Culqi checkout error")
      );
    } else {
      onError?.(new Error("Culqi checkout closed without completing payment"));
    }
  };

  return checkout;
}

function useCulqiCheckout() {
  async function openCheckout(options: CulqiCheckoutOptions): Promise<string> {
    await loadCheckoutJs();

    return new Promise(async (resolve, reject) => {
      const checkout = await loadCulqiCheckout({
        ...options,
        onComplete: resolve,
        onError: reject,
      });

      checkout.open();
    });
  }

  return openCheckout;
}

export default useCulqiCheckout;
export { loadCulqiCheckout };
export type { CulqiCheckoutOptions };
